/** @file Option model for Harmony Select components Represents a single selectable option in NativeSelect or Dropdown */

/**
 * Represents a single selectable option with generic value type
 *
 * @template T The type of the option's value
 */
export type HxOption<T = any> = {
  /** Unique identifier for the option Used for tracking selections and rendering optimization */
  id: string | number;

  /** Display label shown to the user */
  label: string;

  /** The actual value to be stored when this option is selected */
  value: T;

  /** Optional subtitle text to display */
  subtitle?: string;

  /**
   * Whether this option is disabled and cannot be selected
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether this option is pre-selected by default
   *
   * @default false
   */
  isSelected?: boolean;

  /**
   * Numeric score assigned to this option. Used for scoring-enabled scenarios where selecting an option contributes to
   * a total score calculation.
   *
   * @example
   *   ```typescript
   *   const options: HxOption<string>[] = [
   *     { id: 1, label: 'Excellent', value: 'excellent', score: 100 },
   *     { id: 2, label: 'Good', value: 'good', score: 75 },
   *     { id: 3, label: 'Poor', value: 'poor', score: 0 }
   *   ];
   *   ```;
   */
  score?: number;

  /** Optional group identifier for organizing options Used when options are part of an HxOptionGroup */
  groupId?: string | number;

  /** Optional custom data for application-specific needs */
  metadata?: Record<string, any>;

  /** Optional icon identifier to display with the option */
  icon?: string;

  /** Optional title for hover tooltip (defaults to label if not provided) */
  title?: string;

  /** Optional CSS classes for custom styling */
  className?: string;
};

/** Type guard to check if an object is a valid HxOption */
export function isHxOption<T>(obj: any): obj is HxOption<T> {
  return obj && typeof obj === 'object' && 'id' in obj && 'label' in obj && 'value' in obj;
}

/** Helper function to create a typed option */
export function createOption<T>(
  id: string | number,
  label: string,
  value: T,
  options?: Partial<Omit<HxOption<T>, 'id' | 'label' | 'value'>>
): HxOption<T> {
  return {
    id,
    label,
    value,
    ...options,
  };
}

/**
 * Gets the score for a selected option value from a list of options. Returns 0 if no option matches or if the option
 * has no score defined.
 *
 * @example
 *   ```typescript
 *   const options = [
 *     { id: 1, label: 'Yes', value: 'yes', score: 10 },
 *     { id: 2, label: 'No', value: 'no', score: 0 }
 *   ];
 *   const score = getSelectedScore(options, 'yes'); // Returns 10
 *   ```;
 *
 * @param options - Array of HxOption to search
 * @param selectedValue - The currently selected value
 * @returns The score of the matching option, or 0 if not found
 */
export function getSelectedScore<T>(options: HxOption<T>[], selectedValue: T | null | undefined): number {
  if (selectedValue === null || selectedValue === undefined) return 0;
  const option = options.find((o) => o.value === selectedValue);
  return option?.score ?? 0;
}

/**
 * Checks if a given option is semantically representing an "Other" choice, based on common naming patterns in labels
 * and values (case-insensitively).
 *
 * @param option The option to check
 * @param otherValue Optional custom target other value to strictly match
 */
export function isOtherOption<T>(option: { label?: string; value?: any }, otherValue?: T): boolean {
  if (otherValue !== undefined && option.value === otherValue) {
    return true;
  }
  const label = (option.label ?? '').trim().toLowerCase();
  const valueStr = (option.value ?? '').toString().trim().toLowerCase();

  return (
    label === 'other' ||
    valueStr === 'other' ||
    label === 'others' ||
    valueStr === 'others' ||
    label.startsWith('other ') ||
    label.startsWith('other:') ||
    label.startsWith('other-') ||
    valueStr.startsWith('other_') ||
    valueStr.startsWith('other-')
  );
}
